import {
  SPI,
  DialogViewActionWidget,
  BaseActionWidget,
  FormWidget,
  Widget,
  REFRESH_FORM_DATA,
  WidgetSubjection,
  type ObjectValue,
  DeleteOneActionWidget,
  ModelDefaultActionName,
  deepClone
} from '@kunlun/dependencies';
import comp from './MatchProducts.vue';
import { backendNotSelfOperatedItemMutation, type IMatchProductApiRst, matchProductApi } from './service';
import { useAddItemSku } from '@/custom-widget/MindMap/hooks';

@SPI.ClassFactory(
  BaseActionWidget.Token({
    model: 'deli.retail.RetailItemSku',
    name: 'uiViewcdedc658e41a4130acc431e6faf33ffc'
  })
)
@SPI.ClassFactory(
  BaseActionWidget.Token({
    model: 'deli.retail.RetailItemSku',
    name: 'uiView67247f698f6c4f6c8618a5b17456c6c2'
  })
)
export class MatchProducts extends DialogViewActionWidget {
  // 定义需要更新的字段列表（可根据实际需求调整）
  private updateFields = ['barCode', 'standardRetailPrice', 'weightUnit', 'weight'];

  initialize(props: any) {
    super.initialize(props);
    this.setComponent(comp);
    return this;
  }

  @Widget.SubContext(REFRESH_FORM_DATA)
  protected reloadFormData$!: WidgetSubjection<boolean>;

  @Widget.Reactive()
  get formData() {
    return this.rootData?.[0] ?? {};
  }

  @Widget.Reactive()
  public setFormData(val: any) {
    const { itemSkuHeaderList } = useAddItemSku();

    for (const key in val) {
      if (key === 'retailCategory') {
        if (!this.formData[key]) {
          this.formData.retailCategory = val[key];
        }
      } else if (key === 'itemSkus') {
        const existingItemSkus = [...((this.rootData?.[0]?.itemSkus as IMatchProductApiRst['itemSkus']) ?? [])];
        const newItemSkus = (val[key] as IMatchProductApiRst['itemSkus']) ?? [];

        // 处理attrs字段（保留原有逻辑）
        let processedNewSkus = [...newItemSkus];
        if (itemSkuHeaderList.value.length > 0) {
          const attrs = itemSkuHeaderList.value.filter((v) => v.showDelIcon).map((z) => ({ attrName: z.attrName, attrValue: '', showDelIcon: true }));
          processedNewSkus = processedNewSkus.map((v) => {
            const currentAttrs =
              v.attrs?.map((z, j) => {
                return { attrName: z?.attrName ?? '规格', attrValue: z?.attrValue ?? '', showDelIcon: false };
              }) ?? [];
            return {
              ...v,
              attrs: [...currentAttrs, ...deepClone(attrs)]
            };
          });
        } else {
          processedNewSkus = processedNewSkus.map((v) => ({
            ...v,
            attrs: v.attrs?.map((z, j) => {
              return { attrName: z?.attrName ?? '规格', attrValue: z?.attrValue ?? '', showDelIcon: j !== 0 };
            })
          }));
        }

        // 遍历新数据，处理去重和字段更新
        processedNewSkus.forEach((newSku) => {
          const matchKey = this.isSelfOperated ? 'erpCode' : 'barCode';
          const matchValue = newSku[matchKey];
          // 查找是否存在重复项
          const existingSkuIndex = existingItemSkus.findIndex((sku) => sku[matchKey] === matchValue);
          if (existingSkuIndex > -1) {
            // 存在重复项 - 更新指定字段
            this.updateFields.forEach((field) => {
              // 只更新有值的字段（可选，根据需求调整）
              if (newSku[field] !== undefined) {
                existingItemSkus[existingSkuIndex][field] = newSku[field];
              }
            });
          } else {
            existingItemSkus.push(newSku);
          }
        });

        // 更新rootData中的数据
        this.rootData![0].itemSkus = existingItemSkus;
      } else {
        this.rootData![0][key] = val[key];
      }
    }
    this.reloadFormData$.subject.next(true);
    itemSkuHeaderList.value = [];
  }
  @Widget.Reactive()
  public get isSelfOperated() {
    return this.action.name == 'uiView67247f698f6c4f6c8618a5b17456c6c2';
  }

  @Widget.Reactive()
  public async validationAndFillFunction(code: string) {
    const meituanCategory: IMatchProductApiRst['meituanCategory'] = (this.formData?.meituanCategory ?? {}) as IMatchProductApiRst['meituanCategory'];
    const variables: ObjectValue = { path: this.getDsl().sessionPath };

    if (this.action.name == 'uiView67247f698f6c4f6c8618a5b17456c6c2') {
      const itemSkus = ((this.formData?.itemSkus as any) ?? [])
        .map((v) => {
          return { erpCode: v?.erpCode };
        })
        ?.filter((v) => Boolean(v?.erpCode));
      return await matchProductApi(
        {
          erpCodeStr: code,
          name: this.formData.name as string,
          detailUrls: (this.formData?.detailUrls as string[]) ?? [],
          picsUrls: (this.formData.picsUrls as string[]) ?? [],
          meituanCategory: meituanCategory,
          meituanAppSpuCode: (this.formData.meituanAppSpuCode as string) ?? '',
          retailCategory: this.formData.retailCategory as any[],
          itemSkus: itemSkus
        },
        variables
      );
    }
    if (this.action.name == 'uiViewcdedc658e41a4130acc431e6faf33ffc') {
      return await backendNotSelfOperatedItemMutation(
        {
          barCode: code,
          name: this.formData.name as string,
          meituanCategory: meituanCategory,
          meituanAppSpuCode: (this.formData.meituanAppSpuCode as string) ?? '',
          retailCategory: this.formData.retailCategory as any[]
        },
        variables
      );
    }
  }
}

@SPI.ClassFactory(
  BaseActionWidget.Token({
    model: 'deli.retail.RetailItemSku',
    name: ModelDefaultActionName.$$internal_DeleteOne,
    inline: true,
    widget: 'ClientAction'
  })
)
export class MatchProductsDeleteOneActionWidget extends DeleteOneActionWidget {
  protected async clickAction() {
    if (this.activeRecords) {
      if (this.rootData?.[0] && (this.rootData[0]?.itemSkus as any)?.length === this.activeRecords.length) {
        const formWidget = this.getParentWidget?.()?.getParentWidget()?.getParentWidget()?.getParentWidget();
        let excludeFields = [] as any[];
        if (formWidget instanceof FormWidget) {
          excludeFields = formWidget
            ?.getChildrenWidget()
            .filter((w: any) => w.readonly || w.disabled)
            .map((w: any) => w.field?.name);
        }

        clearAllFieldsInPlace(this.rootData?.[0], excludeFields);
      }
      super.clickAction();
    }
  }
}

function clearAllFieldsInPlace(obj: any, excludeFields: string[] = []): void {
  if (obj === null || obj === undefined) return;

  if (Array.isArray(obj)) {
    obj.length = 0; // 清空数组
    return;
  }

  if (typeof obj !== 'object') return;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (excludeFields.includes(key)) {
        continue;
      }

      if (typeof value === 'string') {
        obj[key] = '';
      } else if (typeof value === 'number') {
        obj[key] = 0;
      } else if (typeof value === 'boolean') {
        obj[key] = false;
      } else {
        clearAllFieldsInPlace(value, excludeFields); // 递归处理
      }
    }
  }
}
